import React from 'react';
import { Typography, Card, Space, Divider, Alert, Timeline, List } from 'antd';
import { ShieldCheck, Lock, Eye, Server, Code, MessageCircle, Settings, Bell } from 'lucide-react';

const { Title, Paragraph, Text } = Typography;

const PrivacyPolicy = () => {
    const effectiveDate = "October 30, 2024";

    const dataCollectionPoints = [
        {
            title: "Meeting Content and Transcripts",
            items: [
                "Audio transcriptions and meeting content",
                "Participant interaction patterns",
                "Meeting timestamps and duration",
                "Discussion topics and keywords",
                "Action items and decisions made"
            ]
        },
        {
            title: "User Information",
            items: [
                "Account and profile information",
                "Email address and authentication data",
                "User preferences and settings",
                "Organization and team affiliations",
                "Usage patterns and interaction history"
            ]
        },
        {
            title: "Technical Data",
            items: [
                "Device information and browser type",
                "IP addresses and network identifiers",
                "Performance metrics and error logs",
                "API interaction data",
                "Extension configuration settings"
            ]
        }
    ];

    const securityMeasures = [
        {
            icon: <Lock className="text-blue-600" size={24} />,
            title: "End-to-End Encryption",
            description: "All meeting data is encrypted in transit and at rest using industry-standard AES-256 encryption."
        },
        {
            icon: <Server className="text-blue-600" size={24} />,
            title: "Secure Infrastructure",
            description: "Our infrastructure is hosted on AWS with SOC 2 Type II compliance and regular security audits."
        },
        {
            icon: <Code className="text-blue-600" size={24} />,
            title: "Access Controls",
            description: "Strict role-based access control (RBAC) with multi-factor authentication for all systems."
        },
        {
            icon: <Eye className="text-blue-600" size={24} />,
            title: "Data Privacy",
            description: "Your meeting data is processed in isolated environments with zero data persistence."
        }
    ];

    return (
        <div className="max-w-5xl mx-auto">
            <Card className="shadow-lg border-0">
                <Space direction="vertical" size="large" className="w-full">
                    {/* Header Section */}
                    <div className="text-center pb-8">
                        <Title level={2}>Privacy Policy</Title>
                        <Text type="secondary">Effective Date: {effectiveDate}</Text>
                    </div>

                    <Alert
                        message="Your Privacy Matters"
                        description="At EliCorp, we take your privacy seriously. This policy describes our commitment to protecting your data while delivering exceptional meeting intelligence through our ELI service."
                        type="info"
                        showIcon
                        className="mb-8"
                    />

                    {/* Introduction */}
                    <section>
                        <Title level={3}>1. Introduction and Scope</Title>
                        <Paragraph className="text-justify">
                            This Privacy Policy ("Policy") applies to all services provided by ELICorp, Inc. ("ELICorp," "we," "our," or "us"), including our ELI meeting copilot service, web applications, browser extensions, and related services (collectively, the "Services"). By using our Services, you acknowledge that you have read and understood this Policy.
                        </Paragraph>
                    </section>

                    <Divider />

                    {/* Data Collection */}
                    <section>
                        <Title level={3}>2. Data Collection and Processing</Title>
                        <Space direction="vertical" size="middle" className="w-full">
                            {dataCollectionPoints.map((category, index) => (
                                <Card key={index} className="bg-gray-50 border-0">
                                    <Title level={4}>{category.title}</Title>
                                    <List
                                        size="small"
                                        dataSource={category.items}
                                        renderItem={(item) => (
                                            <List.Item>
                                                <Text>{item}</Text>
                                            </List.Item>
                                        )}
                                    />
                                </Card>
                            ))}
                        </Space>
                    </section>

                    <Divider />

                    {/* Security Measures */}
                    <section>
                        <Title level={3}>3. Security Measures</Title>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {securityMeasures.map((measure, index) => (
                                <Card key={index} className="border-0 shadow-sm">
                                    <Space>
                                        {measure.icon}
                                        <div>
                                            <Text strong>{measure.title}</Text>
                                            <br />
                                            <Text type="secondary">{measure.description}</Text>
                                        </div>
                                    </Space>
                                </Card>
                            ))}
                        </div>
                    </section>

                    <Divider />

                    {/* Data Usage Timeline */}
                    <section>
                        <Title level={3}>4. Data Lifecycle</Title>
                        <Timeline mode="left">
                            <Timeline.Item dot={<MessageCircle size={16} />}>
                                <Text strong>Collection</Text>
                                <br />
                                Data is collected during meetings through secure channels
                            </Timeline.Item>
                            <Timeline.Item dot={<Server size={16} />}>
                                <Text strong>Processing</Text>
                                <br />
                                Real-time processing in isolated environments
                            </Timeline.Item>
                            <Timeline.Item dot={<Settings size={16} />}>
                                <Text strong>Analysis</Text>
                                <br />
                                AI-powered insights generation with privacy preservation
                            </Timeline.Item>
                            <Timeline.Item dot={<Bell size={16} />}>
                                <Text strong>Deletion</Text>
                                <br />
                                Automatic data removal after processing
                            </Timeline.Item>
                        </Timeline>
                    </section>

                    <Divider />

                    {/* Legal Rights */}
                    <section>
                        <Title level={3}>5. Your Legal Rights</Title>
                        <Card className="bg-gray-50 border-0">
                            <List
                                dataSource={[
                                    "Right to Access - Request details about your personal data",
                                    "Right to Rectification - Correct inaccurate data",
                                    "Right to Erasure - Request deletion of your data",
                                    "Right to Restriction - Limit how we use your data",
                                    "Right to Data Portability - Receive and transfer your data",
                                    "Right to Object - Contest our data processing"
                                ]}
                                renderItem={(item) => (
                                    <List.Item>
                                        <Text>{item}</Text>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </section>

                    <Divider />

                    {/* Contact Information */}
                    <section>
                        <Title level={3}>6. Contact Information</Title>
                        <Card className="bg-blue-50 border-0">
                            <Space direction="vertical">
                                <Text>EliCorp, Inc.</Text>
                                <Text>Email: elimeetagent@gmail.com</Text>
                                <Text>Address: 131 Continental Dr
                                    Newark, DE 19713
                                    United States</Text>
                                <Text>Tel: +82 (010) 5919-7473</Text>
                            </Space>
                        </Card>
                    </section>

                    <Divider />

                    {/* Footer */}
                    <footer className="text-center">
                        <Text type="secondary">
                            © {new Date().getFullYear()} EliCorp Labs, Inc. All rights reserved.
                            <br />
                            This Privacy Policy was last updated on {effectiveDate}
                        </Text>
                    </footer>
                </Space>
            </Card>
        </div>
    );
};

export default PrivacyPolicy;